import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, CalendarClock, MessageCircle } from "lucide-react";
import { EASE, Eyebrow, MaskLines } from "./Motion";
import { CONTACT, waLink } from "../../lib/catalog";
import { scrollToId } from "../../lib/api";

const Petal = ({ className, color = "#E91E63", delay = "0s" }) => (
  <svg viewBox="0 0 40 60" className={`petal pointer-events-none absolute ${className}`} style={{ animationDelay: delay }} aria-hidden>
    <path d="M20 2C32 16 34 38 20 58 6 38 8 16 20 2z" fill={color} />
  </svg>
);

const Badge = () => (
  <div className="absolute -left-6 bottom-10 z-20 h-32 w-32 sm:h-36 sm:w-36" data-testid="hero-booking-badge">
    <div className="absolute inset-0 rounded-full bg-[#240620] ring-1 ring-[#D4AF37]/50" />
    <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0">
      <defs><path id="circ" d="M100,100 m-74,0 a74,74 0 1,1 148,0 a74,74 0 1,1 -148,0" /></defs>
      <text className="font-cinzel" fontSize="15" letterSpacing="4" fill="#F3E5AB">
        <textPath href="#circ">THREE DAYS PRIOR BOOKING ◆ DOORSTEP ◆</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center font-display text-4xl italic text-gold-foil">3d</div>
  </div>
);

const Stage = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 110, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div onMouseMove={move} onMouseLeave={reset} className="relative mx-auto w-full max-w-[460px]" style={{ perspective: 1200 }} data-testid="hero-stage">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.08 }}
          animate={{ clipPath: "inset(0% 0 0 0)", scale: 1 }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.35 }}
          className="arch relative aspect-[4/5] overflow-hidden ring-1 ring-[#D4AF37]/60"
        >
          <img src="/brand/hero-main.jpg?v=3" alt="Mini décor floral canopy by PetalBasket" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#190317]/70 via-transparent to-transparent" />
          <p className="absolute bottom-5 left-0 right-0 text-center font-cinzel text-[11px] tracking-[0.35em] text-[#F3E5AB]">MINI DECOR · CANOPY</p>
        </motion.div>
        <div className="arch pointer-events-none absolute -inset-3 border border-[#D4AF37]/35" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: EASE, delay: 1.1 }}
          style={{ transform: "translateZ(70px)" }}
          className="absolute -right-4 top-10 w-32 overflow-hidden rounded-full ring-4 ring-[#240620] sm:-right-10 sm:w-40"
        >
          <img src="/brand/hero-side.jpg" alt="Fresh lotus and jasmine garlands" className="aspect-square w-full object-cover" />
        </motion.div>
        <div className="pointer-events-none absolute inset-0"><Badge /></div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} className="grain relative overflow-hidden bg-[#240620] pt-[100px] md:pt-[150px]" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#E91E63]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/15 blur-[120px]" />
      <Petal className="left-[6%] top-[22%] w-5 opacity-60" />
      <Petal className="left-[46%] top-[16%] w-4 opacity-50" color="#D4AF37" delay="-3s" />
      <Petal className="right-[8%] bottom-[18%] w-6 opacity-50" color="#F57C00" delay="-5s" />

      <div className="relative mx-auto grid max-w-[1440px] gap-10 px-5 pb-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-16">
        <motion.div style={{ y, opacity: fade }} className="lg:col-span-7 lg:pt-6">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <Eyebrow>Fresh flowers ◆ Traditional touch ◆ Beautifully yours</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-display font-medium leading-[0.88] tracking-tight text-[#FFF8EF] text-[16vw] sm:text-[5.5rem] lg:text-[7.4rem] xl:text-[8.4rem]" data-testid="hero-title">
            <MaskLines
              onLoad
              delay={0.15}
              lines={[
                "Celebrate",
                <span key="b" className="italic text-gold-foil">every moment</span>,
                <span key="c">with flowers<span className="text-[#E91E63]">.</span></span>,
              ]}
            />
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.9 }} className="mt-8 max-w-xl">
            <p className="text-base leading-relaxed text-[#E8D8C8] sm:text-lg">
              <span className="font-semibold text-[#FFF8EF]">{CONTACT.company}</span> by LeoFair Events &amp; Entertainments — hand-crafted
              mini decors, thomala, garlands, dindu and door décor, made fresh and delivered to your doorstep.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#E91E63]/50 bg-[#E91E63]/10 px-4 py-2 text-sm text-[#FFF8EF]" data-testid="hero-booking-notice">
              <CalendarClock size={16} className="text-[#F3E5AB]" /> Three days prior booking required for home delivery
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <button onClick={() => scrollToId("collections")} data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 rounded-full bg-gold-foil px-7 py-4 text-sm font-semibold text-[#240620] transition-transform duration-300 hover:-translate-y-0.5">
                Explore the collection <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-[-45deg]" />
              </button>
              <a href={waLink("Hi PetalBasket, I'd like to book flower décor.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full border border-[#F3E5AB]/40 px-7 py-4 text-sm font-semibold text-[#FFF8EF] transition-colors duration-300 hover:border-[#25D366] hover:text-[#25D366]">
                <MessageCircle size={18} /> Enquire on WhatsApp
              </a>
            </div>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pt-4">
          <Stage />
        </div>
      </div>
    </section>
  );
};
